import styled from 'styled-components';

import { portPillBodyCss } from './portPillTheme';

const FONT_SANS =
  "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

/** Row containing the "Image" field label above the slot. */
export const ImageLabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
`;

/**
 * The main image preview slot.
 *
 * Sizing modes:
 *   - Unresized + empty:  uses a fixed landscape aspect-ratio so the
 *     placeholder has a sensible default shape.
 *   - Unresized + filled: aspect-ratio is removed; the <SlotImage>
 *     inside drives the container height from its natural ratio.
 *   - Resized ($overrideWidth/$overrideHeight set): both dimensions
 *     are fixed in pixels; aspect-ratio forced to `auto` so the slot
 *     respects the explicit size exactly.
 */
export const ImageSlot = styled.div<{
  $filled: boolean;
  $overrideWidth?: number;
  $overrideHeight?: number;
}>`
  position: relative;
  width: ${({ $overrideWidth }) => ($overrideWidth ? `${$overrideWidth}px` : '100%')};
  aspect-ratio: ${({ $filled, $overrideHeight }) =>
    $filled || $overrideHeight ? 'auto' : '1.4142 / 1'};
  ${({ $overrideHeight }) => ($overrideHeight ? `height: ${$overrideHeight}px;` : '')}
  border-radius: 14px;
  overflow: hidden;
  background-color: ${({ theme: { colors = {} } }) =>
    colors.surface ?? '#0E0F18'};
  border: 1px solid
    ${({ theme: { colors = {} } }) => colors.surfaceVariant ?? '#25263A'};
  cursor: ${({ $filled }) => ($filled ? 'zoom-in' : 'default')};

  /* Hover/focus reveals the action overlay + resize handle. */
  &:hover > [data-image-actions],
  &:focus-within > [data-image-actions] {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
    pointer-events: auto;
  }
  &:hover > [data-image-resize],
  &:focus-within > [data-image-resize] {
    opacity: 1;
    pointer-events: auto;
  }
`;

/**
 * The generated image rendered inside ImageSlot.
 *
 * When unresized ($sized = false), height:auto lets the image's natural
 * aspect ratio drive the slot's height — no letter-box bars.
 *
 * When the slot has been resized ($sized = true), height:100% +
 * object-fit:cover fills the explicit dimensions exactly. The resize
 * handler locks aspect ratio to the slot's current shape so cover never
 * actually crops anything visible.
 */
export const SlotImage = styled.img<{ $sized?: boolean }>`
  display: block;
  width: 100%;
  ${({ $sized }) =>
    $sized ? 'height: 100%; object-fit: cover;' : 'height: auto;'}
`;

/**
 * Empty-state centred icon wrapper (shown when no image has been
 * generated). Muted onSurfaceVariant pulled in towards the slot's
 * surface so the placeholder icon reads as decoration, not content.
 */
export const ImageEmptyState = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  width: 30%;
  max-width: 88px;
  margin: auto;
  color: ${({ theme: { colors = {} } }) =>
    `color-mix(in oklch, ${colors.onSurfaceVariant ?? '#A1A3B8'} 55%, ${colors.surface ?? '#0E0F18'})`};
  line-height: 0;

  svg {
    display: block;
    width: 100%;
    height: auto;
  }
`;

/**
 * Hover overlay container. Anchored to the bottom-centre of the image
 * slot, fades in on hover, hidden at rest.
 */
export const ActionOverlay = styled.div`
  position: absolute;
  left: 50%;
  bottom: 8px;
  transform: translateX(-50%) translateY(2px);
  display: flex;
  flex-direction: row;
  gap: 4px;
  margin: 0;
  opacity: 0;
  pointer-events: none;
  transition: opacity 140ms ease, transform 140ms ease;
`;

/** Single circular icon button in the hover overlay. */
export const ActionButton = styled.button<{ $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 999px;
  /* Translucent scrim over the image so the button reads on any photo. */
  background: ${({ theme: { colors = {} } }) =>
    `color-mix(in oklch, ${colors.surface ?? '#0E0F18'} 72%, transparent)`};
  border: none;
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.45 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
  transition: background 120ms ease, color 120ms ease, transform 120ms ease;

  .material-symbols-rounded {
    font-size: 16px;
    line-height: 16px;
    width: 16px;
    height: 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-variation-settings: 'FILL' 0, 'wght' 500, 'GRAD' 0, 'opsz' 20;
    opacity: 0.9;
  }

  &:hover:not(:disabled) {
    background: ${({ theme: { colors = {} } }) =>
      `color-mix(in oklch, ${colors.reverse ?? '#FFFFFF'} 18%, transparent)`};

    .material-symbols-rounded {
      opacity: 1;
    }
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }
`;

/** "IMAGE" field label — matches FieldLabel typography minus the bottom margin. */
export const InlineLabelText = styled.div`
  font-size: 10.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#A1A3B8'};
`;

/**
 * Bottom-right drag handle — visible on hover of the image slot.
 * The drag_handle icon is rotated -45° to form a diagonal resize arrow.
 */
export const ResizeHandle = styled.button`
  position: absolute;
  right: 4px;
  bottom: 4px;
  width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  border: 0;
  background: ${({ theme: { colors = {} } }) =>
    `color-mix(in oklch, ${colors.surface ?? '#0E0F18'} 78%, transparent)`};
  color: ${({ theme: { colors = {} } }) =>
    `color-mix(in oklch, ${colors.reverse ?? '#FFFFFF'} 85%, transparent)`};
  cursor: nwse-resize;
  opacity: 0;
  transition: opacity 140ms ease;
  touch-action: none;
  z-index: 3;
  padding: 0;

  .material-symbols-rounded {
    font-size: 14px;
    transform: rotate(-45deg);
  }
`;

/**
 * Instruction textarea — same visual language as nodeCommon's TextArea
 * with a fixed minimum row count for the image-gen layout.
 */
export const InstructionTextarea = styled.textarea.attrs({
  className: 'nowheel',
})`
  width: 100%;
  box-sizing: border-box;
  background: ${({ theme: { colors = {} } }) =>
    colors.surfaceContainerHigh ?? '#1F2030'};
  border: 1px solid
    ${({ theme: { colors = {} } }) => colors.surfaceVariant ?? '#25263A'};
  border-radius: 14px;
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
  font: 400 13px/1.45 ${FONT_SANS};
  padding: 10px 14px;
  outline: none;
  resize: vertical;
  min-height: 64px;
  max-height: 180px;
  transition: border-color 120ms ease, background 120ms ease;

  &::placeholder {
    color: ${({ theme: { colors = {} } }) =>
      `color-mix(in oklch, ${colors.onSurfaceVariant ?? '#A1A3B8'} 65%, transparent)`};
    opacity: 1;
  }

  &:focus {
    border-color: ${({ theme: { colors = {} } }) =>
      colors.primary ?? '#C1C1FF'};
  }
`;

/**
 * Outer wrapper for the instruction label + textarea block. Provides the
 * top gap between the image slot above and the "Instruction" field label.
 * Separate from InstructionRow so the pill positioning context (which
 * lives on InstructionRow) is unaffected by the gap.
 */
export const InstructionSection = styled.div`
  margin-top: 14px;
`;

/** Wrapper for the model-picker dropdown, sitting between image slot and instruction. */
export const ModelSection = styled.div`
  margin-top: 14px;
`;

/**
 * Wrapper around the instruction textarea — also the relative positioning
 * context for FloatingOutputPill, which sits centered on the textarea's
 * vertical midpoint.
 */
export const InstructionRow = styled.div`
  position: relative;
  margin-top: 6px;
`;

/**
 * Floating output port pill — mirrors OutputRowPill exactly but
 * positioned to float off the right edge of the node card.
 *
 * The InstructionRow is a direct child of NodeCardShell's Body which
 * has 18px horizontal padding, so InstructionRow's right edge sits
 * 18px inside the card's right edge. We need right: -(18+11)=-29px to
 * push the pill's centre exactly to the card's right edge (same as
 * OutputRowPill which uses right:-11px in a full-bleed OutputRowList).
 *
 * Idle palette mixes primary into surfaceVariant for a muted look;
 * hover / selected swap to the brighter primaryEmphasis tone. The
 * recipe mirrors OutputRowPill so both port pills stay identical.
 */
export const FloatingOutputPill = styled.div`
  ${portPillBodyCss(-29)}
`;
